import React, { useEffect, useState } from "react";

const LANGUAGE_STORAGE_KEY = "zedro_academy_language";

function getInitialLanguage() {
  if (typeof window === "undefined") return "es";

  const savedLanguage = window.localStorage.getItem(LANGUAGE_STORAGE_KEY);
  return ["es", "fr"].includes(savedLanguage) ? savedLanguage : "es";
}

const OPERATOR = {
  platform: "ZEDRO Academy",
  legalOwnerName: "Rodríguez Díaz, Catalina",
  legalTradeName: "Académie ZEDRO",
  addressEs: "412-9165 rue Lennon, Brossard, Québec, Canadá, J4Y 0R9",
  addressFr: "412-9165, rue Lennon, Brossard (Québec) J4Y 0R9, Canada",
  privacyEmail: "admin@zedro.academy",
  supportEmail: "support@zedro.academy",
  websiteLabel: "zedro.academy",
  websiteUrl: "https://zedro.academy",
};

const PRIVACY_COPY = {
  es: {
    title: "Política de Privacidad",
    effectiveDate: "Entrada en vigor: 17 de julio de 2026",
    lastUpdated: "Última actualización: 17 de julio de 2026",
    intro: [
      <>
        Esta Política explica cómo <strong>{OPERATOR.platform}</strong>,
        plataforma operada bajo el nombre comercial registrado{" "}
        <strong>{OPERATOR.legalTradeName}</strong>, recopila, utiliza, conserva,
        protege y comunica información personal.
      </>,
      <>
        Se aplica al sitio <a href={OPERATOR.websiteUrl}>{OPERATOR.websiteLabel}</a>,
        sus páginas públicas, cuentas, formularios, compras, membresías,
        comunicaciones, herramientas y demás servicios digitales ofrecidos por
        {" "}{OPERATOR.platform}.
      </>,
    ],
    sections: [
      {
        title: "1. Responsable de la protección de la información personal",
        paragraphs: [
          <>
            La responsable de la protección de la información personal es{" "}
            <strong>{OPERATOR.legalOwnerName}</strong>, titular de{" "}
            <strong>{OPERATOR.legalTradeName}</strong>.
          </>,
        ],
        list: [
          <>
            Cargo: <strong>Responsable de la protección de la información personal</strong>
          </>,
          <>
            Correo de privacidad: <a href={`mailto:${OPERATOR.privacyEmail}`}>{OPERATOR.privacyEmail}</a>
          </>,
          <>
            Dirección comercial y postal: <strong>{OPERATOR.addressEs}</strong>
          </>,
        ],
        paragraphsAfter: [
          <>
            Para ejercer un derecho o presentar una inquietud sobre privacidad,
            escribe a <a href={`mailto:${OPERATOR.privacyEmail}`}>{OPERATOR.privacyEmail}</a>{" "}
            con el asunto “Privacidad - ZEDRO Academy”.
          </>,
        ],
      },
      {
        title: "2. Cómo recopilamos la información",
        paragraphs: [
          <>
            Podemos recopilar información directamente cuando una persona crea
            una cuenta, completa un formulario, realiza una compra, utiliza una
            herramienta, se comunica con nosotros o se suscribe a una comunicación.
          </>,
          <>
            También podemos recopilar información técnica mediante el sitio, la
            aplicación, cookies, registros de seguridad y servicios tecnológicos
            necesarios para operar la plataforma.
          </>,
          <>
            Algunos proveedores pueden recopilar o procesar información por
            cuenta de {OPERATOR.legalTradeName}, únicamente para prestar los
            servicios descritos en esta Política.
          </>,
        ],
      },
      {
        title: "3. Información que podemos recopilar",
        paragraphs: [
          <>
            La información depende del servicio utilizado. No todas las categorías
            se aplican a todas las personas.
          </>,
        ],
        groups: [
          {
            subtitle: "3.1 Cuenta e identificación",
            items: [
              "Nombre o nombre visible, cuando se proporcione.",
              "Correo electrónico.",
              "Credenciales técnicas de autenticación y datos de seguridad de la cuenta.",
              "Estado de la cuenta, rol, permisos, fecha de creación y preferencias de idioma.",
            ],
          },
          {
            subtitle: "3.2 Compras, membresías y pagos",
            items: [
              "Producto o servicio adquirido.",
              "Estado de la compra, membresía o suscripción.",
              "Historial de activación, prueba gratuita cuando corresponda, renovación, reactivación, cancelación o expiración.",
              "Identificadores de cliente o transacción proporcionados por el proveedor de pago.",
              "Información limitada sobre facturación, moneda, impuestos, periodicidad y estado del pago.",
            ],
          },
          {
            subtitle: "3.3 Información ingresada en la plataforma",
            intro:
              "Según la herramienta utilizada, una persona puede decidir escribir, registrar, cargar o guardar:",
            items: [
              "Respuestas, reflexiones y notas personales.",
              "Registros de progreso, actividades, ejercicios y evidencias.",
              "Archivos o contenidos aportados voluntariamente.",
              "Preferencias, selecciones e historial de uso de herramientas internas.",
            ],
            outro:
              "La persona decide qué información ingresar. Recomendamos no aportar datos que no sean necesarios, documentos de identidad, números financieros completos, información médica detallada ni información de terceros sin autorización.",
          },
          {
            subtitle: "3.4 Información técnica y de uso",
            items: [
              "Dirección IP y datos de conexión.",
              "Tipo de dispositivo, navegador, sistema operativo y configuración técnica.",
              "Páginas consultadas, acciones realizadas, fecha y hora de acceso.",
              "Registros de autenticación, errores, seguridad y funcionamiento.",
              "Cookies, identificadores técnicos y tecnologías similares.",
            ],
          },
          {
            subtitle: "3.5 Comunicaciones",
            items: [
              "Mensajes enviados a soporte o a los correos corporativos.",
              "Solicitudes relacionadas con cuenta, compras, membresías, pagos, privacidad o soporte.",
              "Preferencias de comunicación y constancia del consentimiento, cuando corresponda.",
              "Información relacionada con la suscripción o baja de ZEDRO News.",
            ],
          },
        ],
      },
      {
        title: "4. Para qué utilizamos la información",
        paragraphs: [<>Podemos utilizar la información para:</>],
        list: [
          "Crear, administrar, autenticar y proteger cuentas.",
          "Proporcionar productos, membresías, contenidos, herramientas y servicios solicitados.",
          "Guardar progreso, preferencias, respuestas y registros elegidos por la persona.",
          "Procesar compras, suscripciones, renovaciones, cancelaciones, reembolsos y soporte de facturación.",
          "Enviar comunicaciones necesarias sobre cuenta, seguridad, acceso, transacciones, cambios importantes y soporte.",
          "Enviar comunicaciones informativas o promocionales cuando exista consentimiento u otra base permitida por la ley.",
          "Mantener, corregir, proteger y mejorar la plataforma.",
          "Realizar medición y analítica conforme a las preferencias de cookies.",
          "Prevenir fraude, abuso, accesos no autorizados e incidentes de seguridad.",
          "Cumplir obligaciones legales, fiscales, contables, regulatorias y contractuales.",
          "Establecer, ejercer o defender reclamaciones y resolver disputas.",
        ],
      },
      {
        title: "5. Consentimiento, opciones y consecuencias",
        paragraphs: [
          <>
            Cuando la ley exige consentimiento, este debe ser claro, libre,
            informado y otorgado para finalidades específicas.
          </>,
          <>
            La persona puede abstenerse de ingresar información opcional o de
            utilizar una herramienta concreta. Algunas funciones no podrán
            prestarse sin los datos necesarios para crear una cuenta, completar
            una compra, guardar un registro o responder una solicitud.
          </>,
          <>
            Las cookies no esenciales pueden rechazarse o modificarse desde las
            preferencias de cookies. Esto puede limitar determinadas mediciones o
            funciones no esenciales, pero no impide el uso básico del sitio.
          </>,
          <>
            Cuando el tratamiento dependa del consentimiento, este puede retirarse
            para usos futuros escribiendo a{" "}
            <a href={`mailto:${OPERATOR.privacyEmail}`}>{OPERATOR.privacyEmail}</a>.
            El retiro no afecta el tratamiento realizado legítimamente antes de
            recibir la solicitud.
          </>,
        ],
      },
      {
        title: "6. Información potencialmente sensible",
        paragraphs: [
          <>
            {OPERATOR.platform} no exige diagnósticos médicos, documentos de
            identidad, datos biométricos, números financieros completos ni otra
            información altamente sensible que no sea necesaria para utilizar el servicio.
          </>,
          <>
            Algunas reflexiones o registros voluntarios pueden revelar creencias,
            circunstancias personales o información íntima. Cuando una persona
            decide ingresar ese contenido, se utiliza para prestar la función
            solicitada, conservarlo en su cuenta, brindar soporte cuando lo pida
            y cumplir obligaciones de seguridad o legales.
          </>,
          <>
            No deben ingresarse datos sensibles innecesarios ni información
            personal de terceros sin autorización.
          </>,
        ],
      },
      {
        title: "7. Cookies y tecnologías similares",
        paragraphs: [
          <>
            Utilizamos cookies y tecnologías similares para funciones técnicas,
            seguridad, inicio de sesión, preferencias de idioma, medición,
            analítica y mejora de la experiencia.
          </>,
          <>
            Las cookies necesarias se utilizan para operar la plataforma. Las
            cookies no esenciales se gestionan conforme a las preferencias de la persona.
          </>,
        ],
        link: {
          before: "Consulta los detalles y modifica tus preferencias en la ",
          text: "Política de Cookies",
          href: "/legal/cookies",
          after: ".",
        },
      },
      {
        title: "8. Quién puede acceder y proveedores utilizados",
        paragraphs: [
          <>
            Dentro de {OPERATOR.legalTradeName}, la información puede ser
            consultada únicamente por la titular y por colaboradores o personas
            autorizadas que necesiten acceder para prestar soporte, administrar
            la plataforma, facturar, mantener la seguridad o cumplir obligaciones legales.
          </>,
          <>
            Actualmente, los proveedores tecnológicos pueden incluir:
          </>,
        ],
        list: [
          "Stripe, para pagos, facturación, prevención de fraude, disputas y contracargos.",
          "Hostinger y Render, para alojamiento del sitio, la aplicación, el servidor y la base de datos.",
          "Cloudflare R2, para almacenamiento de archivos y recursos digitales.",
          "Resend, para el envío de correos transaccionales y comunicaciones autorizadas.",
          "Google Analytics y Google Tag Manager, para medición y analítica cuando las preferencias de cookies lo permiten.",
          "OpenAI, cuando una función asistida por inteligencia artificial es activada por la persona usuaria.",
        ],
        paragraphsAfter: [
          <>
            Estos proveedores reciben únicamente la información necesaria para
            prestar su servicio y están sujetos a sus propias obligaciones
            contractuales, legales y de seguridad.
          </>,
          <>
            {OPERATOR.legalTradeName} no vende ni alquila información personal.
          </>,
        ],
      },
      {
        title: "9. Tratamiento fuera de Québec",
        paragraphs: [
          <>
            Algunos proveedores pueden almacenar o procesar información fuera de
            Québec, incluso en otras provincias de Canadá, Estados Unidos u otros
            países. En esos casos, la información puede quedar sujeta a las leyes
            del lugar donde sea procesada.
          </>,
          <>
            Cuando la ley lo exige, antes de comunicar información personal fuera
            de Québec se evalúan los factores relativos a la privacidad y se
            establecen medidas contractuales y de seguridad razonables.
          </>,
        ],
      },
      {
        title: "10. Pagos y datos financieros",
        paragraphs: [
          <>
            Los pagos son procesados por Stripe u otro proveedor habilitado.
            {" "}{OPERATOR.platform} puede recibir información limitada como el
            identificador del cliente, el producto adquirido, el estado del pago,
            la moneda, los impuestos, las fechas, la periodicidad, la cancelación
            o el reembolso.
          </>,
          <>
            {OPERATOR.platform} no almacena números completos de tarjetas ni
            códigos completos de seguridad del método de pago.
          </>,
          <>
            En caso de fraude, disputa, contracargo, error o solicitud de
            facturación, podremos utilizar la información razonablemente necesaria
            para revisar la transacción, el acceso, la aceptación de condiciones y
            las comunicaciones relacionadas.
          </>,
        ],
      },
      {
        title: "11. Comunicaciones y ZEDRO News",
        paragraphs: [
          <>
            Podemos enviar mensajes necesarios relacionados con una cuenta,
            compra, membresía, seguridad, facturación, acceso, cambios esenciales
            o soporte. Estas comunicaciones operativas no son promocionales.
          </>,
          <>
            ZEDRO News y otras comunicaciones comerciales se envían cuando existe
            consentimiento u otra base permitida por la ley. Cada mensaje incluye
            la identificación y el mecanismo de baja correspondientes.
          </>,
          <>
            La baja de comunicaciones promocionales no impide el envío de mensajes
            necesarios para operar una cuenta o completar una transacción.
          </>,
        ],
      },
      {
        title: "12. Funciones asistidas por inteligencia artificial",
        paragraphs: [
          <>
            Algunas funciones pueden utilizar inteligencia artificial para ayudar
            a organizar, reformular, resumir o procesar contenido proporcionado por
            la persona dentro de una experiencia específica.
          </>,
          <>
            Al utilizar una función asistida por IA, la información necesaria puede
            ser procesada por un proveedor tecnológico. No deben ingresarse datos
            altamente sensibles o innecesarios.
          </>,
          <>
            Los resultados pueden contener errores y no sustituyen asesoría médica,
            psicológica, legal, financiera, contable, profesional o pastoral personalizada.
          </>,
        ],
      },
      {
        title: "13. Conservación y eliminación",
        paragraphs: [
          <>
            Conservamos la información durante el tiempo necesario para cumplir las
            finalidades descritas, prestar el servicio, mantener la seguridad,
            responder solicitudes, resolver disputas y cumplir obligaciones legales,
            fiscales, contables o contractuales.
          </>,
          <>
            Los periodos varían según la categoría de información. Cuando deja de
            ser necesaria, se elimina, destruye de forma segura o anonimiza conforme
            a la ley y a los ciclos técnicos aplicables.
          </>,
          <>
            La información incluida en copias de seguridad puede permanecer durante
            un periodo limitado hasta su sustitución o eliminación programada y no
            se utiliza para nuevas finalidades.
          </>,
        ],
      },
      {
        title: "14. Seguridad e incidentes de confidencialidad",
        paragraphs: [
          <>
            Aplicamos medidas técnicas, administrativas y organizativas razonables,
            proporcionales a la naturaleza de la información y del servicio, como
            controles de acceso, protección de credenciales, gestión de permisos,
            proveedores especializados y prácticas de seguridad.
          </>,
          <>
            Ningún sistema puede garantizar seguridad o disponibilidad absoluta.
            La persona usuaria también debe proteger sus credenciales, dispositivos y sesiones.
          </>,
          <>
            Ante un incidente de confidencialidad, se adoptan medidas razonables
            para contenerlo, reducir riesgos, investigar y prevenir situaciones
            similares. Cuando exista riesgo de perjuicio serio, se notificará a la
            Commission d’accès à l’information du Québec y a las personas afectadas
            conforme a la ley.
          </>,
        ],
      },
      {
        title: "15. Derechos de las personas",
        paragraphs: [
          <>Sujeto a las condiciones y excepciones previstas por la ley, puedes solicitar:</>,
        ],
        list: [
          "Acceso a la información personal que conservamos sobre ti.",
          "Corrección de información inexacta, incompleta o equívoca.",
          "Retiro del consentimiento para usos futuros cuando el tratamiento dependa de ese consentimiento.",
          "Eliminación o cese de determinados usos cuando corresponda legalmente.",
          "Información sobre las categorías de personas que tienen acceso y el periodo de conservación aplicable.",
          "Portabilidad de información informatizada recopilada directamente de ti, cuando este derecho sea aplicable.",
          "Cese de difusión o desindexación cuando se cumplan las condiciones legales.",
        ],
        paragraphsAfter: [
          <>
            Podemos solicitar información razonable para verificar la identidad.
            La responsable responderá dentro de los 30 días siguientes a la
            recepción de una solicitud completa, salvo una excepción prevista por la ley.
          </>,
          <>
            Si una solicitud es rechazada, se comunicarán por escrito los motivos y
            los recursos disponibles conforme a la ley.
          </>,
        ],
      },
      {
        title: "16. Personas menores de edad",
        paragraphs: [
          <>
            {OPERATOR.platform} está dirigida principalmente a personas adultas y
            no busca recopilar intencionalmente información personal de menores de
            14 años.
          </>,
          <>
            Cuando la ley lo exige, la recopilación de información personal de una
            persona menor de 14 años requiere el consentimiento del titular de la
            autoridad parental o del tutor, salvo una excepción legal aplicable.
          </>,
          <>
            Si crees que se recopiló información de una persona menor de 14 años sin
            autorización válida, escribe a{" "}
            <a href={`mailto:${OPERATOR.privacyEmail}`}>{OPERATOR.privacyEmail}</a>.
          </>,
        ],
      },
      {
        title: "17. Cambios a esta Política",
        paragraphs: [
          <>
            Podemos actualizar esta Política para reflejar cambios legales,
            tecnológicos, operativos, comerciales o en los servicios ofrecidos.
            La versión vigente se publica en el sitio con su fecha de actualización.
          </>,
          <>
            Cuando un cambio sea relevante, se comunicará mediante un aviso visible,
            correo electrónico u otro medio apropiado para llegar a las personas afectadas.
          </>,
        ],
      },
      {
        title: "18. Preguntas, solicitudes y reclamaciones",
        paragraphs: [
          <>
            Para preguntas, solicitudes o reclamaciones sobre privacidad, escribe a{" "}
            <a href={`mailto:${OPERATOR.privacyEmail}`}>{OPERATOR.privacyEmail}</a>{" "}
            con el asunto “Privacidad - ZEDRO Academy”.
          </>,
          <>
            Para soporte general relacionado con una cuenta o el servicio, escribe a{" "}
            <a href={`mailto:${OPERATOR.supportEmail}`}>{OPERATOR.supportEmail}</a>.
          </>,
          <>
            Si consideras que una solicitud no fue atendida adecuadamente, puedes
            presentar una reclamación ante la Commission d’accès à l’information du Québec.
          </>,
        ],
      },
    ],
  },

  fr: {
    title: "Politique de confidentialité",
    effectiveDate: "Entrée en vigueur : 17 juillet 2026",
    lastUpdated: "Dernière mise à jour : 17 juillet 2026",
    intro: [
      <>
        La présente Politique explique comment <strong>{OPERATOR.platform}</strong>,
        une plateforme exploitée sous le nom commercial enregistré{" "}
        <strong>{OPERATOR.legalTradeName}</strong>, recueille, utilise, conserve,
        protège et communique des renseignements personnels.
      </>,
      <>
        Elle s’applique au site <a href={OPERATOR.websiteUrl}>{OPERATOR.websiteLabel}</a>,
        à ses pages publiques, comptes, formulaires, achats, abonnements,
        communications, outils et autres services numériques offerts par{" "}
        {OPERATOR.platform}.
      </>,
    ],
    sections: [
      {
        title: "1. Responsable de la protection des renseignements personnels",
        paragraphs: [
          <>
            La responsable de la protection des renseignements personnels est{" "}
            <strong>{OPERATOR.legalOwnerName}</strong>, titulaire d’
            <strong>{OPERATOR.legalTradeName}</strong>.
          </>,
        ],
        list: [
          <>
            Titre : <strong>Responsable de la protection des renseignements personnels</strong>
          </>,
          <>
            Courriel relatif à la vie privée :{" "}
            <a href={`mailto:${OPERATOR.privacyEmail}`}>{OPERATOR.privacyEmail}</a>
          </>,
          <>
            Adresse commerciale et postale : <strong>{OPERATOR.addressFr}</strong>
          </>,
        ],
        paragraphsAfter: [
          <>
            Pour exercer un droit ou soumettre une préoccupation en matière de vie
            privée, écrivez à{" "}
            <a href={`mailto:${OPERATOR.privacyEmail}`}>{OPERATOR.privacyEmail}</a>{" "}
            en indiquant dans l’objet « Confidentialité - ZEDRO Academy ».
          </>,
        ],
      },
      {
        title: "2. Modes de collecte",
        paragraphs: [
          <>
            Nous pouvons recueillir des renseignements directement lorsqu’une
            personne crée un compte, remplit un formulaire, effectue un achat,
            utilise un outil, communique avec nous ou s’abonne à une communication.
          </>,
          <>
            Nous pouvons également recueillir des renseignements techniques au moyen
            du site, de l’application, de cookies, de journaux de sécurité et de
            services technologiques nécessaires au fonctionnement de la plateforme.
          </>,
          <>
            Certains fournisseurs peuvent recueillir ou traiter des renseignements
            pour le compte d’{OPERATOR.legalTradeName}, uniquement afin de fournir
            les services décrits dans la présente Politique.
          </>,
        ],
      },
      {
        title: "3. Renseignements que nous pouvons recueillir",
        paragraphs: [
          <>
            Les renseignements dépendent du service utilisé. Toutes les catégories
            ne s’appliquent pas à toutes les personnes.
          </>,
        ],
        groups: [
          {
            subtitle: "3.1 Compte et identification",
            items: [
              "Nom ou nom affiché, lorsqu’il est fourni.",
              "Adresse courriel.",
              "Données techniques d’authentification et de sécurité du compte.",
              "État du compte, rôle, permissions, date de création et préférences linguistiques.",
            ],
          },
          {
            subtitle: "3.2 Achats, abonnements et paiements",
            items: [
              "Produit ou service acheté.",
              "État de l’achat, de l’adhésion ou de l’abonnement.",
              "Historique d’activation, d’essai gratuit le cas échéant, de renouvellement, de réactivation, d’annulation ou d’expiration.",
              "Identifiants de client ou de transaction fournis par le fournisseur de paiement.",
              "Renseignements limités sur la facturation, la devise, les taxes, la fréquence de facturation et l’état du paiement.",
            ],
          },
          {
            subtitle: "3.3 Renseignements saisis dans la plateforme",
            intro:
              "Selon l’outil utilisé, une personne peut choisir de rédiger, d’enregistrer, de téléverser ou de conserver :",
            items: [
              "Des réponses, réflexions et notes personnelles.",
              "Des registres de progression, activités, exercices et preuves.",
              "Des fichiers ou contenus fournis volontairement.",
              "Des préférences, sélections et historiques d’utilisation d’outils internes.",
            ],
            outro:
              "La personne choisit les renseignements qu’elle saisit. Nous recommandons de ne pas fournir de données inutiles, de documents d’identité, de numéros financiers complets, de renseignements médicaux détaillés ni de renseignements concernant des tiers sans autorisation.",
          },
          {
            subtitle: "3.4 Renseignements techniques et d’utilisation",
            items: [
              "Adresse IP et données de connexion.",
              "Type d’appareil, navigateur, système d’exploitation et configuration technique.",
              "Pages consultées, actions effectuées ainsi que date et heure d’accès.",
              "Journaux d’authentification, d’erreurs, de sécurité et de fonctionnement.",
              "Cookies, identifiants techniques et technologies similaires.",
            ],
          },
          {
            subtitle: "3.5 Communications",
            items: [
              "Messages transmis au soutien ou aux adresses courriel de l’entreprise.",
              "Demandes liées au compte, aux achats, aux abonnements, aux paiements, à la vie privée ou au soutien.",
              "Préférences de communication et preuve du consentement, le cas échéant.",
              "Renseignements liés à l’abonnement ou au désabonnement de ZEDRO News.",
            ],
          },
        ],
      },
      {
        title: "4. Finalités d’utilisation",
        paragraphs: [<>Nous pouvons utiliser les renseignements afin de :</>],
        list: [
          "Créer, administrer, authentifier et protéger les comptes.",
          "Fournir les produits, abonnements, contenus, outils et services demandés.",
          "Conserver la progression, les préférences, les réponses et les registres choisis par la personne.",
          "Traiter les achats, abonnements, renouvellements, annulations, remboursements et demandes liées à la facturation.",
          "Transmettre les communications nécessaires concernant le compte, la sécurité, l’accès, les transactions, les changements importants et le soutien.",
          "Transmettre des communications informatives ou promotionnelles lorsqu’il existe un consentement ou une autre base permise par la loi.",
          "Maintenir, corriger, protéger et améliorer la plateforme.",
          "Effectuer des mesures et de l’analytique conformément aux préférences relatives aux cookies.",
          "Prévenir la fraude, les abus, les accès non autorisés et les incidents de sécurité.",
          "Respecter les obligations juridiques, fiscales, comptables, réglementaires et contractuelles.",
          "Établir, exercer ou défendre des réclamations et régler des différends.",
        ],
      },
      {
        title: "5. Consentement, choix et conséquences",
        paragraphs: [
          <>
            Lorsque la loi exige un consentement, celui-ci doit être manifeste,
            libre, éclairé et donné à des fins précises.
          </>,
          <>
            La personne peut choisir de ne pas saisir de renseignements facultatifs
            ou de ne pas utiliser un outil donné. Certaines fonctions ne peuvent
            toutefois pas être fournies sans les renseignements nécessaires pour
            créer un compte, compléter un achat, conserver un registre ou répondre
            à une demande.
          </>,
          <>
            Les cookies non essentiels peuvent être refusés ou modifiés dans les
            préférences relatives aux cookies. Ce choix peut limiter certaines
            mesures ou fonctions non essentielles, sans empêcher l’utilisation de
            base du site.
          </>,
          <>
            Lorsque le traitement repose sur le consentement, celui-ci peut être
            retiré pour l’avenir en écrivant à{" "}
            <a href={`mailto:${OPERATOR.privacyEmail}`}>{OPERATOR.privacyEmail}</a>.
            Le retrait n’a pas d’effet sur un traitement antérieur effectué licitement.
          </>,
        ],
      },
      {
        title: "6. Renseignements potentiellement sensibles",
        paragraphs: [
          <>
            {OPERATOR.platform} n’exige pas de diagnostics médicaux, de documents
            d’identité, de données biométriques, de numéros financiers complets ni
            d’autres renseignements hautement sensibles qui ne sont pas nécessaires
            à l’utilisation du service.
          </>,
          <>
            Certaines réflexions ou certains registres fournis volontairement peuvent
            révéler des croyances, des circonstances personnelles ou des renseignements
            intimes. Lorsqu’une personne choisit de saisir ce contenu, il est utilisé
            pour fournir la fonction demandée, le conserver dans son compte, offrir le
            soutien demandé et respecter les obligations de sécurité ou les obligations légales.
          </>,
          <>
            Aucun renseignement sensible inutile ni renseignement personnel concernant
            un tiers ne doit être saisi sans autorisation.
          </>,
        ],
      },
      {
        title: "7. Cookies et technologies similaires",
        paragraphs: [
          <>
            Nous utilisons des cookies et des technologies similaires pour les
            fonctions techniques, la sécurité, la connexion, les préférences de
            langue, la mesure, l’analytique et l’amélioration de l’expérience.
          </>,
          <>
            Les cookies nécessaires sont utilisés pour faire fonctionner la plateforme.
            Les cookies non essentiels sont gérés selon les préférences de la personne.
          </>,
        ],
        link: {
          before: "Consultez les détails et modifiez vos préférences dans la ",
          text: "Politique de cookies",
          href: "/legal/cookies",
          after: ".",
        },
      },
      {
        title: "8. Personnes ayant accès et fournisseurs utilisés",
        paragraphs: [
          <>
            Au sein d’{OPERATOR.legalTradeName}, les renseignements peuvent être
            consultés uniquement par la titulaire et par les collaborateurs ou
            personnes autorisées qui doivent y accéder afin de fournir le soutien,
            d’administrer la plateforme, de facturer, d’assurer la sécurité ou de
            respecter des obligations légales.
          </>,
          <>Les fournisseurs technologiques actuels peuvent comprendre :</>,
        ],
        list: [
          "Stripe, pour les paiements, la facturation, la prévention de la fraude, les différends et les rétrofacturations.",
          "Hostinger et Render, pour l’hébergement du site, de l’application, du serveur et de la base de données.",
          "Cloudflare R2, pour le stockage de fichiers et de ressources numériques.",
          "Resend, pour l’envoi de courriels transactionnels et de communications autorisées.",
          "Google Analytics et Google Tag Manager, pour la mesure et l’analytique lorsque les préférences relatives aux cookies le permettent.",
          "OpenAI, lorsqu’une fonction assistée par intelligence artificielle est activée par la personne utilisatrice.",
        ],
        paragraphsAfter: [
          <>
            Ces fournisseurs reçoivent uniquement les renseignements nécessaires pour
            rendre leurs services et sont assujettis à leurs propres obligations
            contractuelles, légales et de sécurité.
          </>,
          <>{OPERATOR.legalTradeName} ne vend ni ne loue les renseignements personnels.</>,
        ],
      },
      {
        title: "9. Traitement hors Québec",
        paragraphs: [
          <>
            Certains fournisseurs peuvent stocker ou traiter des renseignements hors
            Québec, notamment dans d’autres provinces du Canada, aux États-Unis ou dans
            d’autres pays. Les renseignements peuvent alors être assujettis aux lois du
            territoire où ils sont traités.
          </>,
          <>
            Lorsque la loi l’exige, avant de communiquer des renseignements personnels
            hors Québec, une évaluation des facteurs relatifs à la vie privée est
            réalisée et des mesures contractuelles et de sécurité raisonnables sont établies.
          </>,
        ],
      },
      {
        title: "10. Paiements et renseignements financiers",
        paragraphs: [
          <>
            Les paiements sont traités par Stripe ou par un autre fournisseur activé.
            {" "}{OPERATOR.platform} peut recevoir des renseignements limités, notamment
            l’identifiant du client, le produit acheté, l’état du paiement, la devise,
            les taxes, les dates, la fréquence de facturation, l’annulation ou le remboursement.
          </>,
          <>
            {OPERATOR.platform} ne conserve pas les numéros complets de carte ni les
            codes de sécurité complets du mode de paiement.
          </>,
          <>
            En cas de fraude, de différend, de rétrofacturation, d’erreur ou de demande
            liée à la facturation, nous pouvons utiliser les renseignements raisonnablement
            nécessaires pour examiner la transaction, l’accès, l’acceptation des conditions
            et les communications connexes.
          </>,
        ],
      },
      {
        title: "11. Communications et ZEDRO News",
        paragraphs: [
          <>
            Nous pouvons transmettre les messages nécessaires concernant un compte,
            un achat, un abonnement, la sécurité, la facturation, l’accès, les changements
            essentiels ou le soutien. Ces communications opérationnelles ne sont pas promotionnelles.
          </>,
          <>
            ZEDRO News et les autres communications commerciales sont envoyées lorsqu’il
            existe un consentement ou une autre base permise par la loi. Chaque message
            comprend les renseignements d’identification et le mécanisme de désabonnement requis.
          </>,
          <>
            Le désabonnement des communications promotionnelles n’empêche pas l’envoi de
            messages nécessaires au fonctionnement d’un compte ou à l’exécution d’une transaction.
          </>,
        ],
      },
      {
        title: "12. Fonctions assistées par intelligence artificielle",
        paragraphs: [
          <>
            Certaines fonctions peuvent utiliser l’intelligence artificielle afin d’aider
            à organiser, reformuler, résumer ou traiter du contenu fourni par la personne
            dans le cadre d’une expérience précise.
          </>,
          <>
            Lorsqu’une fonction assistée par IA est utilisée, les renseignements nécessaires
            peuvent être traités par un fournisseur technologique. Aucun renseignement hautement
            sensible ou inutile ne doit être saisi.
          </>,
          <>
            Les résultats peuvent comporter des erreurs et ne remplacent pas un avis médical,
            psychologique, juridique, financier, comptable, professionnel ou pastoral personnalisé.
          </>,
        ],
      },
      {
        title: "13. Conservation et élimination",
        paragraphs: [
          <>
            Nous conservons les renseignements pendant la période nécessaire pour atteindre
            les finalités décrites, fournir le service, assurer la sécurité, répondre aux demandes,
            régler les différends et respecter les obligations juridiques, fiscales, comptables ou contractuelles.
          </>,
          <>
            Les périodes varient selon la catégorie de renseignements. Lorsqu’ils ne sont plus
            nécessaires, ils sont supprimés, détruits de façon sécuritaire ou anonymisés
            conformément à la loi et aux cycles techniques applicables.
          </>,
          <>
            Les renseignements contenus dans les copies de sauvegarde peuvent demeurer pendant
            une période limitée jusqu’à leur remplacement ou leur suppression programmée et ne
            sont pas utilisés à de nouvelles fins.
          </>,
        ],
      },
      {
        title: "14. Sécurité et incidents de confidentialité",
        paragraphs: [
          <>
            Nous appliquons des mesures techniques, administratives et organisationnelles
            raisonnables, proportionnelles à la nature des renseignements et du service,
            notamment des contrôles d’accès, la protection des identifiants, la gestion des
            permissions, le recours à des fournisseurs spécialisés et des pratiques de sécurité.
          </>,
          <>
            Aucun système ne peut garantir une sécurité ou une disponibilité absolue. La personne
            utilisatrice doit également protéger ses identifiants, ses appareils et ses sessions.
          </>,
          <>
            Lorsqu’un incident de confidentialité survient, des mesures raisonnables sont prises
            afin de le contenir, de réduire les risques, d’enquêter et de prévenir des incidents
            similaires. S’il présente un risque de préjudice sérieux, la Commission d’accès à
            l’information du Québec et les personnes concernées sont avisées conformément à la loi.
          </>,
        ],
      },
      {
        title: "15. Droits des personnes",
        paragraphs: [
          <>Sous réserve des conditions et exceptions prévues par la loi, vous pouvez demander :</>,
        ],
        list: [
          "L’accès aux renseignements personnels que nous détenons à votre sujet.",
          "La rectification de renseignements inexacts, incomplets ou équivoques.",
          "Le retrait du consentement pour l’avenir lorsque le traitement repose sur ce consentement.",
          "La suppression ou la cessation de certaines utilisations lorsque la loi le prévoit.",
          "Des renseignements sur les catégories de personnes ayant accès et sur la période de conservation applicable.",
          "La portabilité des renseignements informatisés recueillis directement auprès de vous, lorsque ce droit s’applique.",
          "La cessation de la diffusion ou la désindexation lorsque les conditions légales sont remplies.",
        ],
        paragraphsAfter: [
          <>
            Nous pouvons demander des renseignements raisonnables afin de vérifier l’identité.
            La responsable répond dans les 30 jours suivant la réception d’une demande complète,
            sous réserve d’une exception prévue par la loi.
          </>,
          <>
            Si une demande est refusée, les motifs et les recours disponibles sont communiqués
            par écrit conformément à la loi.
          </>,
        ],
      },
      {
        title: "16. Personnes mineures",
        paragraphs: [
          <>
            {OPERATOR.platform} s’adresse principalement aux personnes adultes et ne cherche pas
            à recueillir intentionnellement des renseignements personnels de personnes de moins de 14 ans.
          </>,
          <>
            Lorsque la loi l’exige, la collecte de renseignements personnels auprès d’une personne
            de moins de 14 ans nécessite le consentement du titulaire de l’autorité parentale ou du
            tuteur, sous réserve d’une exception légale applicable.
          </>,
          <>
            Si vous croyez que les renseignements d’une personne de moins de 14 ans ont été recueillis
            sans autorisation valable, écrivez à{" "}
            <a href={`mailto:${OPERATOR.privacyEmail}`}>{OPERATOR.privacyEmail}</a>.
          </>,
        ],
      },
      {
        title: "17. Modifications de la présente Politique",
        paragraphs: [
          <>
            Nous pouvons mettre à jour la présente Politique afin de refléter des changements
            juridiques, technologiques, opérationnels, commerciaux ou apportés aux services.
            La version en vigueur est publiée sur le site avec sa date de mise à jour.
          </>,
          <>
            Lorsqu’une modification est importante, elle est communiquée au moyen d’un avis visible,
            d’un courriel ou de tout autre moyen approprié permettant de joindre les personnes concernées.
          </>,
        ],
      },
      {
        title: "18. Questions, demandes et plaintes",
        paragraphs: [
          <>
            Pour toute question, demande ou plainte concernant la vie privée, écrivez à{" "}
            <a href={`mailto:${OPERATOR.privacyEmail}`}>{OPERATOR.privacyEmail}</a>{" "}
            en indiquant dans l’objet « Confidentialité - ZEDRO Academy ».
          </>,
          <>
            Pour le soutien général lié à un compte ou au service, écrivez à{" "}
            <a href={`mailto:${OPERATOR.supportEmail}`}>{OPERATOR.supportEmail}</a>.
          </>,
          <>
            Si vous estimez qu’une demande n’a pas été traitée adéquatement, vous pouvez déposer
            une plainte auprès de la Commission d’accès à l’information du Québec.
          </>,
        ],
      },
    ],
  },
};

const pageStyle = {
  maxWidth: 900,
  margin: "2rem auto",
  padding: "0 1rem",
  fontFamily: "Questrial, sans-serif",
  lineHeight: 1.75,
  color: "#2c3b51",
};

const headingStyle = { color: "#264763" };

function renderParagraphs(paragraphs = []) {
  return paragraphs.map((paragraph, index) => <p key={index}>{paragraph}</p>);
}

function renderList(items = []) {
  return (
    <ul>
      {items.map((item, index) => (
        <li key={index}>{item}</li>
      ))}
    </ul>
  );
}

function renderLink(link) {
  if (!link) return null;

  return (
    <p>
      {link.before}
      <a href={link.href}>{link.text}</a>
      {link.after}
    </p>
  );
}

function renderGroups(groups = []) {
  return groups.map((group, index) => (
    <div key={index}>
      <h3>{group.subtitle}</h3>
      {group.intro && <p>{group.intro}</p>}
      {renderList(group.items)}
      {group.outro && <p>{group.outro}</p>}
    </div>
  ));
}

export default function PrivacyPolicy() {
  const [language, setLanguage] = useState(getInitialLanguage);
  const copy = PRIVACY_COPY[language] || PRIVACY_COPY.es;

  useEffect(() => {
    if (typeof window === "undefined") return;

    function syncLanguage(nextLanguage) {
      if (["es", "fr"].includes(nextLanguage)) {
        setLanguage(nextLanguage);
      } else {
        setLanguage("es");
      }
    }

    function handleLanguageEvent(event) {
      syncLanguage(event?.detail?.language);
    }

    function handleStorage(event) {
      if (event.key === LANGUAGE_STORAGE_KEY) {
        syncLanguage(event.newValue);
      }
    }

    syncLanguage(window.localStorage.getItem(LANGUAGE_STORAGE_KEY));
    window.addEventListener("zedro:languagechange", handleLanguageEvent);
    window.addEventListener("storage", handleStorage);

    return () => {
      window.removeEventListener("zedro:languagechange", handleLanguageEvent);
      window.removeEventListener("storage", handleStorage);
    };
  }, []);

  return (
    <div style={pageStyle}>
      <h1>{copy.title}</h1>

      <p>
        <strong>{copy.effectiveDate}</strong>
        <br />
        <strong>{copy.lastUpdated}</strong>
      </p>

      {renderParagraphs(copy.intro)}

      {copy.sections.map((section) => (
        <section key={section.title}>
          <h2 style={headingStyle}>{section.title}</h2>
          {renderParagraphs(section.paragraphs)}
          {section.list && renderList(section.list)}
          {section.groups && renderGroups(section.groups)}
          {renderLink(section.link)}
          {renderParagraphs(section.paragraphsAfter)}
        </section>
      ))}
    </div>
  );
}
